import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Save, Clock, User, AlertCircle, CheckCircle, BookOpen, Scale } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';


const TABS = [
  { key: 'instructions', label: 'Uputstva za rad', icon: BookOpen, description: 'Uputstva za korišćenje platforme koja se prikazuju korisnicima' },
  { key: 'terms', label: 'Pravila i uslovi korišćenja', icon: Scale, description: 'Pravila i uslovi korišćenja platforme' },
];

const AdminContentEditor = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('instructions');
  const [contents, setContents] = useState({ instructions: '', terms: '' });
  const [meta, setMeta] = useState({ instructions: null, terms: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase?.from('platform_content')?.select('content_type, content, updated_at, updated_by')?.in('content_type', ['instructions', 'terms']);

      if (fetchError) throw fetchError;

      const newContents = { instructions: '', terms: '' };
      const newMeta = { instructions: null, terms: null };

      data?.forEach((row) => {
        newContents[row.content_type] = row?.content || '';
        newMeta[row.content_type] = {
          updated_at: row?.updated_at,
          updated_by: row?.updated_by,
        };
      });

      setContents(newContents);
      setMeta(newMeta);
    } catch (err) {
      setError('Greška pri učitavanju sadržaja: ' + (err?.message || 'Nepoznata greška'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      const { error: upsertError } = await supabase?.from('platform_content')?.upsert(
          {
            content_type: activeTab,
            content: contents?.[activeTab],
            updated_at: new Date()?.toISOString(),
            updated_by: profile?.id || null,
          },
          { onConflict: 'content_type' }
        );

      if (upsertError) throw upsertError;

      setMeta((prev) => ({
        ...prev,
        [activeTab]: {
          updated_at: new Date()?.toISOString(),
          updated_by: profile?.id || null,
        },
      }));
      setSuccessMsg('Sadržaj je uspešno sačuvan!');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setError('Greška pri čuvanju: ' + (err?.message || 'Nepoznata greška'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Nije dostupno';
    return new Date(dateStr)?.toLocaleString('sr-RS', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const currentTab = TABS?.find((t) => t?.key === activeTab);
  const currentMeta = meta[activeTab];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div
        className="flex-1 transition-all duration-300"
        style={{ marginLeft: isSidebarCollapsed ? '80px' : '256px' }}
      >
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Upravljanje sadržajem platforme</h1>
              <p className="text-sm text-gray-500">Uređivanje tekstova koji se prikazuju korisnicima</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-gray-200">
            {TABS?.map((tab) => {
              const Icon = tab?.icon;
              return (
                <button
                  key={tab?.key}
                  onClick={() => { setActiveTab(tab?.key); setError(''); setSuccessMsg(''); }}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab?.key
                      ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab?.label}
                </button>
              );
            })}
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-4 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          {successMsg && (
            <div className="mb-4 flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">{successMsg}</p>
            </div>
          )}

          {/* Editor Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            {/* Card Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-gray-900">{currentTab?.label}</h2>
                <p className="text-sm text-gray-500 mt-0.5">{currentTab?.description}</p>
              </div>
              {currentMeta?.updated_at && (
                <div className="text-right text-xs text-gray-400 space-y-1">
                  <div className="flex items-center gap-1 justify-end">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Poslednja izmena: {formatDate(currentMeta?.updated_at)}</span>
                  </div>
                  {currentMeta?.updated_by && (
                    <div className="flex items-center gap-1 justify-end">
                      <User className="w-3.5 h-3.5" />
                      <span>Admin ID: {currentMeta?.updated_by?.slice(0, 8)}...</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Textarea */}
            <div className="p-6">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : (
                <textarea
                  value={contents?.[activeTab]}
                  onChange={(e) => setContents((prev) => ({ ...prev, [activeTab]: e?.target?.value }))}
                  rows={24}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm font-mono text-gray-800 leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={`Unesite tekst za ${currentTab?.label?.toLowerCase()}...`}
                  disabled={saving}
                />
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50 rounded-b-xl">
              <p className="text-xs text-gray-400">
                Podržan je Markdown format za formatiranje teksta (naslovi sa #, **podebljano**, *kurziv*)
              </p>
              <button
                onClick={handleSave}
                disabled={saving || loading}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Čuvanje...' : 'Sačuvaj izmene'}
              </button>
            </div>
          </div>

          {/* Quick Links */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              href="/uputstva"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors group"
            >
              <BookOpen className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
              <div>
                <p className="text-sm font-medium text-gray-700 group-hover:text-blue-700">Pregledaj uputstva</p>
                <p className="text-xs text-gray-400">Otvori stranicu uputstava kao korisnik</p>
              </div>
            </a>
            <a
              href="/uslovi-koriscenja"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors group"
            >
              <Scale className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
              <div>
                <p className="text-sm font-medium text-gray-700 group-hover:text-blue-700">Pregledaj pravila</p>
                <p className="text-xs text-gray-400">Otvori stranicu uslova kao korisnik</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContentEditor;
